<template>
    <div class="flex h-[100dvh] w-full flex-col lg:flex-row">
        <Teleport to="body">
            <LazySharedProspectSearchModal v-model:open="openAccountFinder" />
        </Teleport>
        <header v-show="!props.hideMobileHeader" class="flex h-12 flex-none items-center gap-2 border-b border-slate-200 bg-white px-3 lg:hidden">
            <UiLogo :expand="true" icon="h-5 w-5" />
            <span class="min-w-0 flex-1 truncate text-sm font-medium text-slate-600">{{ currentMobilePageLabel }}</span>
            <SharedAdminOrganizationSwitcher v-if="isGlobalAdmin" compact class="w-32 flex-none" />
            <UButton icon="i-ion-search" size="sm" color="neutral" variant="ghost" aria-label="Search accounts"
                @click="openAccountFinder = true" />
            <UDropdownMenu :items="[mobileLinks]" :content="{ align: 'end' }">
                <UButton icon="i-ion-menu" size="sm" color="neutral" variant="ghost" aria-label="Open navigation" />
            </UDropdownMenu>
        </header>
        <div class="hidden flex-none h-full border-r border-slate-200 bg-white lg:flex flex-col pl-0.5 pr-1"
            :class="collapsed ? 'w-20' : 'w-52'">
            <template v-if="!collapsed">
                <div class="flex items-center mt-6 flex-none">
                    <UiLogo :expand="true" icon="h-5 w-5 ml-2" />
                    <UButton icon="i-tabler-layout-sidebar-left-collapse" size="sm" class="ml-auto" color="neutral"
                        variant="ghost" aria-label="Collapse sidebar" @click="collapsed = !collapsed" />
                </div>
                <UInput placeholder="Search Account" icon="i-ion-search" class="mt-4 px-1" size="sm"
                    @click="() => openAccountFinder = true">
                    <template #trailing>
                        <UKbd>/</UKbd>
                    </template>
                </UInput>
                <div v-if="showInactiveSubscriptionWarning"
                    class="mx-1 mt-4 rounded-md border border-amber-200 bg-amber-50 p-2 text-amber-900">
                    <div class="flex gap-2">
                        <UIcon name="i-heroicons-exclamation-triangle" class="mt-0.5 h-4 w-4 flex-none text-amber-600" />
                        <div class="min-w-0">
                            <p class="text-xs font-semibold leading-4">Subscription is not active</p>
                            <p class="mt-0.5 text-xs leading-4 text-amber-800">Functionality will be limited for this organization.</p>
                        </div>
                    </div>
                </div>
                <div class="grow overflow-y-auto mt-6 mb-2">
                    <UNavigationMenu orientation="vertical" :items="topLinks" :ui="navUI" />
                    <div class="mt-4"></div>
                    <UNavigationMenu orientation="vertical" :items="secondaryLinks" :ui="navUI" />
                    <NavigationGettingStartedChecklist
                        v-if="showGettingStarted && gettingStartedExpanded"
                        :status="gettingStartedStatus"
                        variant="expanded"
                    />
                    <!-- <p class="font-medium text-xs px-2 text-slate-700 mt-6">Signals</p>
                    <UNavigationMenu :items="signalLinks" :ui="navUI" class="mt-1" />
                    <p class="font-medium text-xs px-2 text-slate-700 mt-6">Enrichments</p>
                    <UNavigationMenu :items="enrichmentLinks" :ui="navUI" class="mt-1" /> -->
                </div>
                <NavigationGettingStartedChecklist
                    v-if="showGettingStarted && !gettingStartedExpanded"
                    :status="gettingStartedStatus"
                    variant="collapsed"
                />
                <UNavigationMenu orientation="vertical" :items="bottomLinks" :ui="navUI" class="mb-2" />

                <a v-if="isProd" href="https://status.coldreach.ai" target="_blank" rel="noopener"
                    class="block overflow-hidden mb-2 ml-2" style="width: 180px; height: 22px;">
                    <iframe src="https://status.coldreach.ai/badge?theme=light" width="250" height="30"
                        frameborder="0" scrolling="no"
                        style="color-scheme: normal; transform: scale(0.7); transform-origin: left top; pointer-events: none;" />
                </a>

                <!-- Organization Switcher for Global Admins -->
                <SharedAdminOrganizationSwitcher v-if="isGlobalAdmin" />
            </template>
            <template v-else>
                <UButton icon="i-tabler-layout-sidebar-left-expand-filled" size="sm" color="neutral"
                    variant="ghost" aria-label="Expand sidebar" @click="collapsed = !collapsed" class="mx-auto mt-6" />
                <UTooltip v-if="showInactiveSubscriptionWarning"
                    text="Subscription is not active. Functionality will be limited for this organization.">
                    <UIcon name="i-heroicons-exclamation-triangle" class="mx-auto mt-4 block h-5 w-5 text-amber-600" />
                </UTooltip>
                <nav aria-label="Primary navigation" class="mt-4 min-h-0 flex flex-col overflow-y-auto">
                    <NuxtLink
                        v-for="link in collapsedLinks"
                        :key="link.to"
                        :to="link.to"
                        class="mx-1.5 flex min-h-14 flex-col items-center justify-center gap-1 rounded-lg px-1 py-2 text-center text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-inset"
                        :class="{ 'bg-blue-50 text-blue-700': isCollapsedLinkActive(link) }"
                    >
                        <UIcon :name="link.icon" class="h-5 w-5" />
                        <span class="text-[10px] font-medium leading-none">
                            {{ link.label }}
                        </span>
                    </NuxtLink>
                </nav>
            </template>
            <div v-if="props.deferAccountHealth" class="mt-auto px-1.5 py-2 text-[11px] text-slate-400" role="status">
                Loading account health…
            </div>
            <NavigationAccountHealth v-else :collapsed="collapsed" class="mt-auto" />
            <div class="border-t border-slate-100 px-2 py-3">
                <UButton color="neutral" variant="ghost" icon="i-ion-log-out-outline" label="Log out" :loading="loggingOut" :disabled="loggingOut" :class="collapsed ? 'flex-col w-full text-[10px]' : 'w-full justify-start'" @click="logOut" />
            </div>
        </div>
        <main class="min-h-0 min-w-0 grow" :class="props.inner">
            <slot></slot>
        </main>
    </div>
</template>

<script setup lang="ts">
const props = defineProps<{
    hideMobileHeader?: boolean
    inner?: string
    deferAccountHealth?: boolean
}>();
const collapsed = useState("sideNav-collapsed", () => false);

// Keep only the global admin check for the organization switcher
const isGlobalAdmin = useGlobalAdmin();
const [sessionData] = useSessionData();
const showInactiveSubscriptionWarning = computed(() => sessionData.value?.orgActive === false);
const isOrgAdmin = useIsOrgAdmin();
const gettingStartedExpanded = useState('getting-started-expanded', () => true);
type GettingStartedStatus = {
    notifications: boolean;
    exclusionList: boolean;
    firstCampaign: boolean;
};
const gettingStartedStatus = ref<GettingStartedStatus>({
    notifications: false,
    exclusionList: false,
    firstCampaign: false,
});
const gettingStartedStatusLoaded = ref(false);
async function refreshGettingStartedStatus() {
    if (!isOrgAdmin.value) return;
    const response = await fetch('/api/onboarding/getting-started');
    if (!response.ok) return;
    gettingStartedStatus.value = await response.json() as GettingStartedStatus;
    gettingStartedStatusLoaded.value = true;
}
const showGettingStarted = computed(() => gettingStartedStatusLoaded.value
    && isOrgAdmin.value
    && Object.values(gettingStartedStatus.value).some(complete => !complete));

const navUI = {
    link: 'pl-2 pr-3 py-1.5 border-l-2 border-transparent before:rounded-none gap-2 text-slate-600 rounded-none data-[active]:border-blue-700',
    linkLeadingIcon: 'w-4 h-4',
};

const topLinks = computed(() => [
    { label: "Coldreach AI", to: "/chat", icon: "i-ion-sparkles" },
    { label: "Campaigns", to: "/campaigns", icon: "i-ion-megaphone", active: route.path.startsWith('/campaigns') },
]);

const route = useRoute();
watch(() => route.fullPath, () => {
    if (isOrgAdmin.value) void refreshGettingStartedStatus().catch(() => undefined);
});
const opensTrackingEnabled = ref(false);

onMounted(async () => {
    void refreshGettingStartedStatus().catch(() => undefined);
    const status = await fetch('/api/opens/status').then(res => res.json()) as { trackingEnabled: boolean };
    opensTrackingEnabled.value = status.trackingEnabled;
});

const secondaryLinks = computed(() => [
    { label: "Replies", to: "/replies", icon: "i-ion-mail-unread" },
    { label: "Schedule", to: "/scheduled", icon: "i-ion-calendar" },
    { label: "Activity", to: "/activity", icon: "i-ion-pulse" },
    ...(opensTrackingEnabled.value ? [
        { label: "Opens", to: "/opens", icon: "i-ion-mail-open" },
    ] : []),
    { label: "Results", to: "/performance", icon: "i-ion-stats-bars" },
    { label: "Insights", to: "/insights", icon: "i-ion-analytics" },
    { label: "Team", to: "/owner-performance", icon: "i-ion-podium" },
]);

const bottomLinks = computed(() => [
    { label: "Settings", icon: "i-ion-settings", to: "/settings/account" },
    ...(isGlobalAdmin.value ? [
        { label: "Admin", icon: "i-heroicons-shield-check", to: "/admin/organizations" },
    ] : []),
]);

type CollapsedNavigationLink = {
    label: string;
    to: string;
    icon: string;
    active?: boolean;
};

const collapsedLinks = computed<CollapsedNavigationLink[]>(() => [
    ...topLinks.value,
    ...secondaryLinks.value,
    ...bottomLinks.value,
]);

const mobileLinks = computed(() => [
    ...collapsedLinks.value.map(link => ({ label: link.label, icon: link.icon, to: link.to })),
    { label: 'Log out', icon: 'i-ion-log-out-outline', disabled: loggingOut.value, onSelect: logOut },
]);
const currentMobilePageLabel = computed(() => collapsedLinks.value.find(isCollapsedLinkActive)?.label ?? 'Coldreach');

function isCollapsedLinkActive(link: CollapsedNavigationLink) {
    if (link.active !== undefined) return link.active;
    return route.path === link.to || route.path.startsWith(`${link.to}/`);
}

const loggingOut = ref(false);
const toast = useToast();
async function logOut() {
    if (loggingOut.value) return;
    loggingOut.value = true;
    try {
        const response = await fetch('/api/auth/session', { method: 'DELETE' });
        if (!response.ok) throw new Error('Logout failed');
        // Full navigation discards cached organization data as well as the cleared cookie.
        window.location.assign('/signin');
    } catch {
        toast.add({ title: 'Could not log out', description: 'Please try again.', color: 'error' });
        loggingOut.value = false;
    }
}

const isProd = import.meta.env.PROD;

const openAccountFinder = ref(false);
defineShortcuts({
    '/': () => openAccountFinder.value = true
});

</script>
